import React from 'react';
import { ChevronUp, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { formatDateTimeDisplay, toDateTimeLocalInputValue, dateTimeLocalInputToIso } from '../../lib/datetime';
import AIProviderChainManager from './AIProviderChainManager';
import AILearningManager from './AILearningManager';

const AITab = ({
  aiAnalysisSettings,
  aiProviderPresetKey,
  selectedAiProviderPreset,
  apiKeyEnvLooksLikeSecret,
  savingAiSettings,
  testingAiSettings,
  aiSettingsTestResult,
  appTimeZone,
  handleAiSettingsField,
  applyAiProviderPreset,
  handleSaveAiSettings,
  handleTestAiSettings,
  aiTradingAgentSettings,
  aiTradingAgentPresetKey,
  selectedAiTradingAgentPreset,
  agentApiKeyEnvLooksLikeSecret,
  savingAiTradingAgentSettings,
  testingAiTradingAgentSettings,
  aiTradingAgentTestResult,
  handleAiTradingAgentField,
  handleAiResearchContextField,
  handleAiResearchTimeframeToggle,
  applyAiTradingAgentPreset,
  handleSaveAiTradingAgentSettings,
  handleTestAiTradingAgentSettings,
  marketNewsEvents,
  savingMarketNewsEvents,
  addMarketNewsEvent,
  removeMarketNewsEvent,
  updateMarketNewsEventField,
  handleSaveMarketNewsEvents,
}) => {
  const [expandedEventId, setExpandedEventId] = React.useState(null);
  return (
    <div className="space-y-6">
      <AIProviderChainManager />
      <AILearningManager />
      <Card className="hidden">
        <CardHeader>
          <CardTitle>AI Market Analysis Provider</CardTitle>
          <CardDescription>
            Choose the LLM backend for dashboard analysis, scheduled futures market summaries, and optional AI-guided entry review.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="font-medium text-white">Enable LLM-backed analysis</div>
                <p className="mt-1 text-sm text-zinc-500">
                  When disabled, the dashboard AI tab falls back to rule-based signal analysis only.
                </p>
              </div>
              <Switch
                checked={Boolean(aiAnalysisSettings.enabled)}
                onCheckedChange={(checked) => handleAiSettingsField('enabled', checked)}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Provider preset</label>
              <Select value={aiProviderPresetKey} onValueChange={applyAiProviderPreset}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="ollama">Ollama (local/free)</SelectItem>
                  <SelectItem value="lm_studio">LM Studio (local)</SelectItem>
                  <SelectItem value="openai_chatgpt">ChatGPT / OpenAI</SelectItem>
                  <SelectItem value="anthropic">Anthropic</SelectItem>
                  <SelectItem value="gemini">Gemini</SelectItem>
                  <SelectItem value="openai_compatible">OpenAI-compatible (generic)</SelectItem>
                  <SelectItem value="custom">Custom LiteLLM model</SelectItem>
                  <SelectItem value="disabled">Disabled / rules only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Display label</label>
              <Input
                value={aiAnalysisSettings.provider_label || ''}
                onChange={(e) => handleAiSettingsField('provider_label', e.target.value)}
                placeholder={selectedAiProviderPreset.provider_label || 'Local Ollama'}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">LiteLLM model string</label>
              <Input
                value={aiAnalysisSettings.model || ''}
                onChange={(e) => handleAiSettingsField('model', e.target.value)}
                placeholder={selectedAiProviderPreset.model || 'ollama/gemma4:e4b'}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">API base URL</label>
              <Input
                value={aiAnalysisSettings.api_base || ''}
                onChange={(e) => handleAiSettingsField('api_base', e.target.value)}
                placeholder={selectedAiProviderPreset.api_base || 'Optional / provider default'}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">API key env var</label>
              <Input
                value={aiAnalysisSettings.api_key_env || ''}
                onChange={(e) => handleAiSettingsField('api_key_env', e.target.value)}
                placeholder={selectedAiProviderPreset.api_key_env || 'OPENAI_API_KEY'}
                className="bg-zinc-800 border-zinc-700"
              />
              <div className={`text-xs ${apiKeyEnvLooksLikeSecret ? 'text-amber-400' : 'text-zinc-500'}`}>
                {apiKeyEnvLooksLikeSecret
                  ? 'This looks like a real API key, not an env var name. Test/save will treat it as a direct key and store it securely.'
                  : 'Use an env var name like OPENAI_API_KEY or GEMINI_API_KEY. If you want to paste the key itself, use API key override.'}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">API key override</label>
              <Input
                type="password"
                value={aiAnalysisSettings.api_key || ''}
                onChange={(e) => handleAiSettingsField('api_key', e.target.value)}
                placeholder={aiAnalysisSettings.api_key_set ? 'Stored key present — leave blank to keep it' : 'Optional direct key'}
                className="bg-zinc-800 border-zinc-700"
              />
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <Switch
                  checked={Boolean(aiAnalysisSettings.clear_api_key)}
                  onCheckedChange={(checked) => handleAiSettingsField('clear_api_key', checked)}
                />
                <span>Clear stored key on save</span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Temperature</label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="1.5"
                value={aiAnalysisSettings.temperature ?? 0.2}
                onChange={(e) => handleAiSettingsField('temperature', e.target.value)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Max tokens</label>
              <Input
                type="number"
                min="64"
                max="2048"
                value={aiAnalysisSettings.max_tokens ?? 400}
                onChange={(e) => handleAiSettingsField('max_tokens', e.target.value)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Timeout (seconds)</label>
              <Input
                type="number"
                min="5"
                max="120"
                value={aiAnalysisSettings.timeout_seconds ?? 20}
                onChange={(e) => handleAiSettingsField('timeout_seconds', e.target.value)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Fallback to rule-based analysis</label>
              <div className="flex h-10 items-center justify-between rounded-md border border-zinc-700 bg-zinc-800 px-3">
                <span className="text-sm text-zinc-300">Keep the AI tab usable if the LLM is offline</span>
                <Switch
                  checked={Boolean(aiAnalysisSettings.fallback_to_rules)}
                  onCheckedChange={(checked) => handleAiSettingsField('fallback_to_rules', checked)}
                />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="font-medium text-white">Use selected LLM for market summaries</div>
                <p className="mt-1 text-sm text-zinc-500">
                  The scheduled summary remains grounded in TradoPro market data and falls back to deterministic text if the provider is unavailable.
                </p>
              </div>
              <Switch
                checked={Boolean(aiAnalysisSettings.use_for_market_summary)}
                disabled={!aiAnalysisSettings.enabled}
                onCheckedChange={(checked) => handleAiSettingsField('use_for_market_summary', checked)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-zinc-400">System prompt</label>
            <textarea
              value={aiAnalysisSettings.system_prompt || ''}
              onChange={(e) => handleAiSettingsField('system_prompt', e.target.value)}
              rows={5}
              className="w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-zinc-500"
            />
          </div>

          <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 text-sm text-zinc-400">
            <div className="font-medium text-white">Recommended starting points</div>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {[
                {
                  key: 'ollama',
                  title: 'Ollama',
                  detail: '`ollama/gemma4:e4b` at `http://host.docker.internal:11434`',
                },
                {
                  key: 'lm_studio',
                  title: 'LM Studio',
                  detail: 'Local OpenAI-compatible endpoint on `http://127.0.0.1:1234/v1`',
                },
                {
                  key: 'openai_chatgpt',
                  title: 'ChatGPT / OpenAI',
                  detail: '`gpt-5-mini` with `OPENAI_API_KEY`',
                },
                {
                  key: 'gemini',
                  title: 'Gemini',
                  detail: '`gemini/gemini-2.5-flash` with `GEMINI_API_KEY`',
                },
              ].map((preset) => {
                const isSelected = aiProviderPresetKey === preset.key;
                return (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => applyAiProviderPreset(preset.key)}
                    className={`rounded-lg border px-3 py-3 text-left transition-colors ${
                      isSelected
                        ? 'border-blue-500/40 bg-blue-500/10 text-blue-100'
                        : 'border-zinc-800 bg-black/20 text-zinc-300 hover:border-zinc-700 hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium">{preset.title}</div>
                      {isSelected ? (
                        <Badge className="bg-blue-500/15 text-blue-200 border-blue-500/30">Selected</Badge>
                      ) : null}
                    </div>
                    <div className="mt-1 text-xs leading-5 opacity-80">{preset.detail}</div>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 text-xs text-zinc-500">
              Hosted free/cheap providers can still use the generic OpenAI-compatible or Custom LiteLLM presets above.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={handleSaveAiSettings} disabled={savingAiSettings}>
              {savingAiSettings ? 'Saving...' : 'Save AI Settings'}
            </Button>
            <Button variant="outline" onClick={handleTestAiSettings} disabled={testingAiSettings}>
              {testingAiSettings ? 'Testing...' : 'Test Connection'}
            </Button>
            <div className="text-sm text-zinc-500">
              Key source: {aiAnalysisSettings.api_key_source || 'none'}
              {aiAnalysisSettings.updated_at ? ` • Updated ${formatDateTimeDisplay(aiAnalysisSettings.updated_at, appTimeZone)}` : ''}
            </div>
          </div>

          {aiSettingsTestResult ? (
            <div className={`rounded-xl border p-4 text-sm ${aiSettingsTestResult.ok ? 'border-emerald-900 bg-emerald-950/40 text-emerald-200' : 'border-red-900 bg-red-950/40 text-red-200'}`}>
              <div className="font-medium">{aiSettingsTestResult.ok ? 'Connection OK' : 'Connection Failed'}</div>
              <div className="mt-1">{aiSettingsTestResult.message}</div>
              {aiSettingsTestResult.model ? (
                <div className="mt-1 text-xs opacity-80">Model: {aiSettingsTestResult.model}</div>
              ) : null}
              {aiSettingsTestResult.preview ? (
                <div className="mt-2 rounded-md bg-black/20 p-2 text-xs">{aiSettingsTestResult.preview}</div>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card className="hidden">
        <CardHeader>
          <CardTitle>Trading Decision Agent</CardTitle>
          <CardDescription>
            Independent hosted LLM used only when an opted-in user has a valid automation entry to review. It never shares the Ollama analysis conversation or bypasses TradoPro safety.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-md border border-amber-900/60 bg-amber-950/20 px-4 py-3">
            <div>
              <div className="font-medium text-white">Enable dedicated trading agent</div>
              <div className="mt-1 text-xs text-amber-200/75">Users must also opt in. Practice, simulation, demo, and evaluation accounts only.</div>
            </div>
            <Switch
              checked={Boolean(aiTradingAgentSettings.enabled)}
              onCheckedChange={(checked) => handleAiTradingAgentField('enabled', checked)}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Agent provider preset</label>
              <Select value={aiTradingAgentPresetKey} onValueChange={applyAiTradingAgentPreset}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="anthropic_fable">Anthropic Claude Fable 5</SelectItem>
                  <SelectItem value="anthropic_sonnet">Anthropic Claude Sonnet 5</SelectItem>
                  <SelectItem value="openai_frontier">OpenAI GPT-5.5</SelectItem>
                  <SelectItem value="openai_latest">OpenAI GPT-5.6 (access required)</SelectItem>
                  <SelectItem value="custom">Custom LiteLLM model</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Display label</label>
              <Input
                value={aiTradingAgentSettings.provider_label || ''}
                onChange={(event) => handleAiTradingAgentField('provider_label', event.target.value)}
                placeholder={selectedAiTradingAgentPreset.provider_label || 'Trading Decision Agent'}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Model ID</label>
              <Input
                value={aiTradingAgentSettings.model || ''}
                onChange={(event) => handleAiTradingAgentField('model', event.target.value)}
                placeholder={selectedAiTradingAgentPreset.model || 'anthropic/claude-fable-5'}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">API base URL</label>
              <Input
                value={aiTradingAgentSettings.api_base || ''}
                onChange={(event) => handleAiTradingAgentField('api_base', event.target.value)}
                placeholder="Provider default"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">API key env var</label>
              <Input
                value={aiTradingAgentSettings.api_key_env || ''}
                onChange={(event) => handleAiTradingAgentField('api_key_env', event.target.value)}
                placeholder={selectedAiTradingAgentPreset.api_key_env || 'ANTHROPIC_API_KEY'}
                className="bg-zinc-800 border-zinc-700"
              />
              {agentApiKeyEnvLooksLikeSecret ? <div className="text-xs text-amber-400">This looks like a direct key; use the protected API key field.</div> : null}
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">API key override</label>
              <Input
                type="password"
                value={aiTradingAgentSettings.api_key || ''}
                onChange={(event) => handleAiTradingAgentField('api_key', event.target.value)}
                placeholder={aiTradingAgentSettings.api_key_set ? 'Stored key present - leave blank to keep it' : 'Optional direct key'}
                className="bg-zinc-800 border-zinc-700"
              />
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <Switch
                  checked={Boolean(aiTradingAgentSettings.clear_api_key)}
                  onCheckedChange={(checked) => handleAiTradingAgentField('clear_api_key', checked)}
                />
                <span>Clear stored key on save</span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Max tokens</label>
              <Input type="number" min="128" max="2048" value={aiTradingAgentSettings.max_tokens ?? 600} onChange={(event) => handleAiTradingAgentField('max_tokens', event.target.value)} className="bg-zinc-800 border-zinc-700" />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Timeout (seconds)</label>
              <Input type="number" min="5" max="120" value={aiTradingAgentSettings.timeout_seconds ?? 30} onChange={(event) => handleAiTradingAgentField('timeout_seconds', event.target.value)} className="bg-zinc-800 border-zinc-700" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-zinc-400">Agent role prompt</label>
            <textarea
              value={aiTradingAgentSettings.system_prompt || ''}
              onChange={(event) => handleAiTradingAgentField('system_prompt', event.target.value)}
              rows={5}
              className="w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-zinc-500"
            />
            <div className="text-xs text-zinc-500">TradoPro appends non-editable direction, sizing, stop, readiness, and queue constraints to every fresh decision request.</div>
          </div>

          <div className="space-y-4 rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
            <div>
              <h4 className="text-sm font-semibold text-white">Decision context &amp; cadence</h4>
              <p className="text-xs text-zinc-500">
                TradoPro builds this context and supplies it to whichever LLM is connected. Values are clamped to safe bounds on save.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Research interval (min)</label>
                <Input type="number" min="1" max="60"
                  value={aiTradingAgentSettings.research_context?.research_interval_minutes ?? 5}
                  onChange={(event) => handleAiResearchContextField('research_interval_minutes', event.target.value)}
                  className="bg-zinc-800 border-zinc-700" />
                <div className="text-xs text-zinc-500">How often a new entry is evaluated. Lower = more LLM calls.</div>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Bars sent per symbol</label>
                <Input type="number" min="10" max="200"
                  value={aiTradingAgentSettings.research_context?.prompt_bars ?? 45}
                  onChange={(event) => handleAiResearchContextField('prompt_bars', event.target.value)}
                  className="bg-zinc-800 border-zinc-700" />
                <div className="text-xs text-zinc-500">M1 bars included for detailed symbols.</div>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Detailed symbols</label>
                <Input type="number" min="1" max="8"
                  value={aiTradingAgentSettings.research_context?.detailed_markets ?? 3}
                  onChange={(event) => handleAiResearchContextField('detailed_markets', event.target.value)}
                  className="bg-zinc-800 border-zinc-700" />
                <div className="text-xs text-zinc-500">How many symbols get full bars vs summary only.</div>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Model context window</label>
                <Input type="number" min="2048" max="32768" step="1024"
                  value={aiTradingAgentSettings.research_context?.model_context_window ?? 16384}
                  onChange={(event) => handleAiResearchContextField('model_context_window', event.target.value)}
                  className="bg-zinc-800 border-zinc-700" />
                <div className="text-xs text-zinc-500">Must exceed the prompt or newest bars get truncated.</div>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Summary window (bars)</label>
                <Input type="number" min="30" max="400"
                  value={aiTradingAgentSettings.research_context?.market_context_bars ?? 150}
                  onChange={(event) => handleAiResearchContextField('market_context_bars', event.target.value)}
                  className="bg-zinc-800 border-zinc-700" />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Bars fetched per symbol</label>
                <Input type="number" min="60" max="600"
                  value={aiTradingAgentSettings.research_context?.market_fetch_bars ?? 260}
                  onChange={(event) => handleAiResearchContextField('market_fetch_bars', event.target.value)}
                  className="bg-zinc-800 border-zinc-700" />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Opening range (min)</label>
                <Input type="number" min="1" max="120"
                  value={aiTradingAgentSettings.research_context?.opening_range_minutes ?? 15}
                  onChange={(event) => handleAiResearchContextField('opening_range_minutes', event.target.value)}
                  className="bg-zinc-800 border-zinc-700" />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Limit order expiry (sec)</label>
                <Input type="number" min="15" max="600"
                  value={aiTradingAgentSettings.research_context?.limit_order_expiry_seconds ?? 75}
                  onChange={(event) => handleAiResearchContextField('limit_order_expiry_seconds', event.target.value)}
                  className="bg-zinc-800 border-zinc-700" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Timeframes supplied to the model</label>
              <div className="flex flex-wrap gap-2">
                {[[1440, '1d'], [240, '4h'], [60, '1h'], [30, '30m'], [15, '15m'], [5, '5m'], [3, '3m'], [2, '2m']].map(([interval, label]) => {
                  const ladder = aiTradingAgentSettings.research_context?.timeframe_ladder || [];
                  const active = ladder.includes(interval);
                  return (
                    <button
                      key={interval}
                      type="button"
                      onClick={() => handleAiResearchTimeframeToggle(interval)}
                      className={`rounded-md border px-3 py-1 text-xs font-semibold transition ${
                        active
                          ? 'border-blue-500/60 bg-blue-500/20 text-blue-200'
                          : 'border-zinc-700 bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="text-xs text-zinc-500">Compact per-timeframe reads. The model decides how to weigh them.</div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={aiTradingAgentSettings.research_context?.include_timeframe_ladder !== false}
                  onCheckedChange={(checked) => handleAiResearchContextField('include_timeframe_ladder', checked)}
                />
                <span className="text-sm text-zinc-400">Include timeframe ladder</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={aiTradingAgentSettings.research_context?.include_intraday_features !== false}
                  onCheckedChange={(checked) => handleAiResearchContextField('include_intraday_features', checked)}
                />
                <span className="text-sm text-zinc-400">Include intraday structure (VWAP, session levels, volume)</span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Trading mission</label>
              <textarea
                value={aiTradingAgentSettings.research_context?.mission_prompt || ''}
                onChange={(event) => handleAiResearchContextField('mission_prompt', event.target.value)}
                rows={5}
                className="w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-zinc-500"
              />
              <div className="text-xs text-zinc-500">The objective TradoPro states to every connected LLM. Leave blank to restore the default. Hard safety limits are enforced in code and are never editable.</div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Data dictionary</label>
              <textarea
                value={aiTradingAgentSettings.research_context?.data_dictionary || ''}
                onChange={(event) => handleAiResearchContextField('data_dictionary', event.target.value)}
                rows={4}
                className="w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-zinc-500"
              />
              <div className="text-xs text-zinc-500">Explains the payload fields so any model can read them. Leave blank to restore the default.</div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={handleSaveAiTradingAgentSettings} disabled={savingAiTradingAgentSettings}>
              {savingAiTradingAgentSettings ? 'Saving...' : 'Save Trading Agent'}
            </Button>
            <Button variant="outline" onClick={handleTestAiTradingAgentSettings} disabled={testingAiTradingAgentSettings}>
              {testingAiTradingAgentSettings ? 'Testing...' : 'Test Trading Agent'}
            </Button>
            <div className="text-sm text-zinc-500">Key source: {aiTradingAgentSettings.api_key_source || 'none'}</div>
          </div>

          {aiTradingAgentTestResult ? (
            <div className={`rounded-md border p-3 text-sm ${aiTradingAgentTestResult.ok ? 'border-emerald-900 bg-emerald-950/40 text-emerald-200' : 'border-red-900 bg-red-950/40 text-red-200'}`}>
              <div className="font-medium">{aiTradingAgentTestResult.ok ? 'Connection OK' : 'Connection Failed'}</div>
              <div className="mt-1">{aiTradingAgentTestResult.message}</div>
              {aiTradingAgentTestResult.preview ? <div className="mt-2 text-xs">{aiTradingAgentTestResult.preview}</div> : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle>Upcoming Market Events</CardTitle>
          <CardDescription>
            Admin-managed event calendar for the dashboard News tab. Times display in the current user timezone, but event timestamps are stored in UTC.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-zinc-500">
              Display timezone: <span className="text-zinc-200">{appTimeZone}</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={addMarketNewsEvent}>Add Event</Button>
              <Button onClick={handleSaveMarketNewsEvents} disabled={savingMarketNewsEvents}>
                {savingMarketNewsEvents ? 'Saving...' : 'Save Events'}
              </Button>
            </div>
          </div>

          {marketNewsEvents.length === 0 ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 text-sm text-zinc-500">
              No upcoming events configured yet.
            </div>
          ) : (
            <div className="space-y-4">
              {marketNewsEvents.map((event) => (
                <div key={event.id} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4 space-y-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="truncate text-sm font-semibold text-white">{event.title || 'Untitled market event'}</div>
                        <Badge
                          variant="outline"
                          className={event.impact === 'high' ? 'border-red-500/30 text-red-300' : event.impact === 'low' ? 'border-zinc-700 text-zinc-400' : 'border-amber-500/30 text-amber-300'}
                        >
                          {event.impact || 'medium'} impact
                        </Badge>
                        {!event.is_active ? <Badge variant="secondary">Inactive</Badge> : null}
                      </div>
                      <div className="mt-1 text-xs text-zinc-500">
                        {event.starts_at
                          ? formatDateTimeDisplay(event.starts_at, appTimeZone, { timeZoneName: 'short' })
                          : 'Choose an event time'}
                        {(event.affected_symbols || []).length ? ` - ${(event.affected_symbols || []).join(', ')}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={Boolean(event.is_active)}
                        onCheckedChange={(checked) => updateMarketNewsEventField(event.id, 'is_active', checked)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-expanded={expandedEventId === event.id}
                        onClick={() => setExpandedEventId((current) => current === event.id ? null : event.id)}
                      >
                        {expandedEventId === event.id ? <ChevronUp className="mr-1 h-4 w-4" /> : <Pencil className="mr-1 h-4 w-4" />}
                        {expandedEventId === event.id ? 'Done' : 'Edit'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => removeMarketNewsEvent(event.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {expandedEventId === event.id ? (
                    <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-sm text-zinc-400">Title</label>
                      <Input
                        value={event.title || ''}
                        onChange={(e) => updateMarketNewsEventField(event.id, 'title', e.target.value)}
                        placeholder="FOMC rate decision"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-zinc-400">Event time</label>
                      <Input
                        type="datetime-local"
                        value={toDateTimeLocalInputValue(event.starts_at)}
                        onChange={(e) => updateMarketNewsEventField(event.id, 'starts_at', dateTimeLocalInputToIso(e.target.value))}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-zinc-400">Impact</label>
                      <Select value={event.impact || 'medium'} onValueChange={(value) => updateMarketNewsEventField(event.id, 'impact', value)}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-zinc-400">Affected instruments</label>
                      <Input
                        value={(event.affected_symbols || []).join(', ')}
                        onChange={(e) => updateMarketNewsEventField(event.id, 'affected_symbols', e.target.value)}
                        placeholder="ES, MES, NQ, MNQ"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-zinc-400">Source</label>
                      <Input
                        value={event.source || ''}
                        onChange={(e) => updateMarketNewsEventField(event.id, 'source', e.target.value)}
                        placeholder="BLS"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-zinc-400">Source URL</label>
                      <Input
                        value={event.source_url || ''}
                        onChange={(e) => updateMarketNewsEventField(event.id, 'source_url', e.target.value)}
                        placeholder="https://..."
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm text-zinc-400">Market impact</label>
                    <textarea
                      value={event.market_impact || ''}
                      onChange={(e) => updateMarketNewsEventField(event.id, 'market_impact', e.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-zinc-500"
                      placeholder="Explain how this event can affect the selected instruments."
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm text-zinc-400">Notes</label>
                    <textarea
                      value={event.notes || ''}
                      onChange={(e) => updateMarketNewsEventField(event.id, 'notes', e.target.value)}
                      rows={2}
                      className="w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-zinc-500"
                      placeholder="Optional trading desk notes."
                    />
                  </div>
                    </>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AITab;
